import { useCallback } from 'react'
import { rem } from 'polished'
import { useIntl } from 'react-intl'
import styled from 'styled-components'
import Group from 'components/utils/Group'
import TextButton from 'LuxKit/Button/TextButton'
import BodyText from 'LuxKit/Typography/BodyText'
import Heading from 'LuxKit/Typography/Heading'
import LineTrashAltIcon from 'LuxKit/Icons/line/LineTrashAltIcon'
import LinePenIcon from 'LuxKit/Icons/line/LinePenIcon'
import Image from 'components/Common/Image'
import PriceRowPrice from 'LuxKit/PricePoints/PriceRowPrice'
import CartPanelAccommodationTileLayout from './CartPanelAccommodationTileLayout'
import useCartPanelHotelOfferData from '../hooks/useCartPanelHotelOfferData'
import type { CartPanelAccommodationItem } from '../hooks/useCartPanelItems'
import { useTripPlannerEventBus } from 'tripPlanner/assistant/contexts/TripPlannerEventBus'

const RoomCard = styled.div`
  border: 1px solid ${props => props.theme.palette.neutral.default.three};
  border-radius: ${props => props.theme.borderRadius.M};
  padding: ${rem(10)} ${rem(12)};
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: ${rem(12)};
  align-self: flex-start;
  width: fit-content;
  min-width: min(${rem(300)}, 100%);
  max-width: 100%;
  container-type: inline-size;
  container-name: room-card;
`

const ChangeRoomButtonWrapper = styled.div`
  @container room-card (max-width: ${rem(360)}) {
    flex-basis: 100%;
    align-self: flex-start;
  }
`

const RoomImageWrapper = styled.div`
  width: ${rem(80)};
  height: ${rem(80)};
  border-radius: ${props => props.theme.borderRadius.S};
  overflow: hidden;
  flex-shrink: 0;
`

const RoomCardContent = styled.div`
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: ${rem(2)};
`

const DurationDetails = styled.div`
  padding: ${rem(10)} ${rem(20)};
  display: grid;
  grid-template-columns: minmax(0, 1fr) auto;
  align-items: center;
  gap: ${rem(12)};
  background: ${props => props.theme.palette.messaging.warning.lightBackground};
`

const DurationDetailsContent = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${rem(2)};
  min-width: 0;
`

const BannerActionButton = styled(TextButton)`
  --color-primary-bg: ${props => props.theme.palette?.neutral?.default?.one ?? '#363A45'};
  --color-primary-hover: ${props => props.theme.palette?.neutral?.default?.one ?? '#363A45'};
  --color-primary-active: ${props => props.theme.palette?.neutral?.default?.one ?? '#363A45'};
  --button-color: ${props => props.theme.palette?.neutral?.default?.one ?? '#363A45'};
  --button-border-color: ${props => props.theme.palette?.neutral?.default?.one ?? '#363A45'};
  --button-hover-color: ${props => props.theme.palette?.neutral?.surface?.darkContrast ?? '#FFFFFF'};
  --button-hover-background-color: ${props => props.theme.palette?.neutral?.default?.one ?? '#363A45'};
  --button-hover-border-color: ${props => props.theme.palette?.neutral?.default?.one ?? '#363A45'};
  --button-active-color: ${props => props.theme.palette?.neutral?.surface?.darkContrast ?? '#FFFFFF'};
  --button-active-background-color: ${props => props.theme.palette?.neutral?.default?.one ?? '#363A45'};
  --button-active-border-color: ${props => props.theme.palette?.neutral?.default?.one ?? '#363A45'};
  --button-focus-color: ${props => props.theme.palette?.neutral?.default?.one ?? '#363A45'};
  --button-focus-border-color: ${props => props.theme.palette?.neutral?.default?.one ?? '#363A45'};
`

function getRoomImage(pkg: App.Package | App.BedbankPackage | undefined): App.Image | undefined {
  if (!pkg) return undefined
  if ('roomType' in pkg) return pkg.roomType?.images?.[0]
  if ('images' in pkg) return pkg.images[0]
  return undefined
}

interface Props {
  item: CartPanelAccommodationItem
  onToggleInclude: (item: CartPanelAccommodationItem) => void
  onDelete: (item: CartPanelAccommodationItem) => void
  isStreaming: boolean
}

function CartPanelDurationUnavailableTile({
  item,
  onToggleInclude,
  onDelete,
  isStreaming,
}: Props) {
  const intl = useIntl()
  const { pkg, checkIn, checkOut } = useCartPanelHotelOfferData(item)
  const eventBus = useTripPlannerEventBus()

  const handleCardClick = useCallback(() => {
    if (!item.offerId) return
    const searchParams = checkIn || checkOut ? { checkIn, checkOut } : undefined
    eventBus.dispatch({ type: 'open_offer_drawer', offerId: item.offerId, searchParams, scrollToRoomOptions: true })
  }, [item.offerId, checkIn, checkOut, eventBus])

  const handleDelete = useCallback(() => {
    onDelete(item)
  }, [onDelete, item])

  const roomImage = getRoomImage(pkg) ?? (item.roomImageId ? { id: item.roomImageId } : undefined)

  const durationLabel = item.duration
    ? intl.formatMessage(
        {
          id: 'vzY1Nv2NAQwUIY-kfaRz',
          defaultMessage: '{nights, plural, one {# night} other {# nights}} not available',
          description: 'Attention indicator showing the current stay duration has no matching hotel rate',
        },
        { nights: item.duration },
      )
    : intl.formatMessage({
        id: 'Xmzbp8R31R0qY7HhjltT',
        defaultMessage: 'Duration not available',
        description: 'Attention indicator when no hotel rate matches the itinerary duration',
      })

  const availableLabel = item.availableDurations?.length
    ? intl.formatMessage(
        {
          id: '-H2Q6-Pr1gjIUdXm3JaQ',
          defaultMessage: 'Available: {durations}',
          description: 'Shows which night counts are available for a hotel when the current duration has no rate',
        },
        {
          durations: item.availableDurations.map(d => intl.formatMessage(
            { id: 'AsP3VSrHhSASl5Qz8Mji', defaultMessage: '{count, plural, one {# night} other {# nights}}', description: 'Night count label for showing available hotel stay durations in the cart panel' },
            { count: d },
          )).join(', '),
        },
      )
    : undefined

  const chooseDatesLabel = intl.formatMessage({
    id: 'V2LE0eq7SuU6LUpelr-n',
    defaultMessage: 'Choose dates',
    description: 'Action button that opens the hotel drawer so the user can choose dates for a duration-unavailable hotel',
  })

  const header = (
    <Group direction="vertical" gap={4}>
      <Heading variant="heading4">{item.name}</Heading>
      {item.dates && <BodyText variant="medium" colour="neutral-three">{item.dates}</BodyText>}
    </Group>
  )

  const selectedRoomCard = (item.roomName || item.occupancy) ? (
    <RoomCard>
      {roomImage && (
        <RoomImageWrapper>
          <Image image={roomImage} width={80} height={80} fit="center" objectFit="cover" dpr={2} />
        </RoomImageWrapper>
      )}
      <RoomCardContent>
        {item.roomName && <BodyText variant="medium" weight="bold">{item.roomName}</BodyText>}
        {item.occupancy && <BodyText variant="medium" colour="neutral-three">{item.occupancy}</BodyText>}
      </RoomCardContent>
      <ChangeRoomButtonWrapper>
        <TextButton kind="tertiary" size="small" startIcon={<LinePenIcon />} onClick={handleCardClick} disabled={isStreaming || !item.offerId}>
          {intl.formatMessage({
            id: 'BrfTYdJonj0A3hETLQR8',
            defaultMessage: 'Change room',
            description: 'Link to change the selected room for a hotel in the cart panel',
          })}
        </TextButton>
      </ChangeRoomButtonWrapper>
    </RoomCard>
  ) : undefined

  const topBanner = (
    <DurationDetails>
      <DurationDetailsContent>
        <BodyText variant="medium" weight="bold">
          {durationLabel}
        </BodyText>
        {availableLabel && <BodyText variant="small" colour="neutral-three">{availableLabel}</BodyText>}
      </DurationDetailsContent>
      <BannerActionButton kind="secondary" size="small" onClick={handleCardClick} disabled={isStreaming || !item.offerId}>
        {chooseDatesLabel}
      </BannerActionButton>
    </DurationDetails>
  )

  const deleteAction = (
    <TextButton kind="secondary" size="small" shape="round" startIcon={<LineTrashAltIcon />} onClick={handleDelete} disabled={isStreaming}>
      {intl.formatMessage({
        id: 'jDEZjhV3eo-cnxIBHOtR',
        defaultMessage: 'Delete',
        description: 'Button to permanently delete an item from the trip planner cart panel',
      })}
    </TextButton>
  )

  const price = item.pricing.price ? (
    <PriceRowPrice
      price={item.pricing.price}
      priceFormat="preciseDollar"
      saleUnit=""
      size="S"
    />
  ) : undefined

  return (
    <CartPanelAccommodationTileLayout
      item={item}
      onToggleInclude={onToggleInclude}
      onCardClick={item.offerId ? handleCardClick : undefined}
      topBanner={topBanner}
      header={header}
      roomCard={selectedRoomCard}
      attention={undefined}
      deleteAction={deleteAction}
      price={price}
    />
  )
}

export default CartPanelDurationUnavailableTile
